/**
 * Pure helpers for `BillingDocumentsLedger.vue` (AB#20717 — Stripe PDF S3-serving +
 * billing-documents ledger). Kept framework-free so they're cheaply unit-testable
 * without mounting the component.
 *
 * Row shapes mirror `order_item_invoices` (see
 * `custimoo/database/migrations/2026_04_21_150000_create_order_item_invoice_ledger_tables.php`)
 * and the Stripe Credit Note v2 eligibility payload (`CreditNoteEligibility` /
 * `CreditNoteExistingCnSummary` in `./stripeCreditNote`).
 */

import {
  minorUnitsToAmount,
  type CreditNoteEligibility,
  type CreditNoteExistingCnSummary,
} from './stripeCreditNote';
import { resolveInvoiceDocumentUrl } from './invoicePdf';

export interface BillingLedgerInvoiceRow {
  kind: 'invoice';
  /** Stable row key, e.g. `inv-42`. */
  id: string;
  invoiceId: number | string | null;
  /** `stripe_invoice_number` preferred, else `stripe_invoice_id`, else `'N/A'`. */
  number: string;
  status: string | null;
  /** `order_item_invoices.created_at` (ISO timestamp) — `null` when absent. */
  createdAt: string | null;
  /** Major-unit decimal (NOT Stripe minor units — see `pickInvoiceAmount`). `null` when undeterminable. */
  amount: number | null;
  /**
   * Frozen PRE-TAX document total (`order_item_invoices.subtotal_excl_tax`, AB#21269) — the
   * excl-tax counterpart of `amount` (which is tax-inclusive `stripe_total`). `null` on rows the
   * hourly sweep hasn't backfilled yet; the Net billed tax breakdown only renders when every row
   * has one (see `netBilled`).
   */
  exclTax: number | null;
  currency: string | null;
  /** Sum of `lineAllocations`/`line_allocations` quantities. `null` when the relation wasn't loaded. */
  invoicedQty: number | null;
  pdfUrl: string;
  /** Original row, kept for callers that need fields this type doesn't surface (e.g. `order_item_id`). */
  raw: Record<string, unknown>;
}

export interface BillingLedgerCreditNoteRow {
  kind: 'credit_note';
  /** Stable row key, e.g. `cn-cn_123`. */
  id: string;
  /** `BillingLedgerInvoiceRow.id` of the invoice this credit note applies to. */
  parentInvoiceId: string;
  number: string | null;
  status: string | null;
  /** Stripe `created` (unix seconds) — `null` when absent. */
  createdAt: number | null;
  /** Major-unit decimal, always <= 0 (negative or zero). */
  amount: number;
  /**
   * PRE-TAX credit figure (`order_customer_credits.subtotal_excl_tax`), negative like `amount`,
   * `null` when the source row doesn't carry it (legacy un-swept rows, eligibility-only rows).
   */
  exclTax: number | null;
  /** How this credit note settled — see {@link CreditNoteSettlement}. */
  settlement: CreditNoteSettlement;
  currency: string | null;
  /** Negative quantity when derivable, else `null` — see `creditQtyFromEligibility`. */
  qty: number | null;
  pdfUrl: string;
  pdfSource: 's3' | 'stripe' | null;
  /**
   * Source record — the embedded `order_item_invoices.credit_notes[]` entry when the payload
   * carries it (preferred, has `id` / `credit_note_pdf_s3_key` for `openCreditNotePdf`), else the
   * eligibility `existing_credit_notes[]` summary. Optional so hand-built rows (tests, callers
   * that only need `amount`/`currency` for `netBilled`) don't need to fabricate one.
   */
  raw?: Record<string, unknown>;
}

export type BillingLedgerRow = BillingLedgerInvoiceRow | BillingLedgerCreditNoteRow;

/**
 * How a credit note settled (AB#21269 — finance couldn't tell refund CNs from pre-payment CNs):
 *  - 'card_refund'  — created after payment; the amount went back to the customer's card
 *  - 'out_of_band'  — created after payment; recorded for manual settlement by finance
 *  - 'pre_payment'  — created before payment; it reduced the invoice's amount due
 *  - null           — settlement unknown (fields absent: old payload, or no local row for the CN)
 */
export type CreditNoteSettlement = 'card_refund' | 'out_of_band' | 'pre_payment' | null;

/**
 * Derive the settlement from the allocation recorded at creation
 * (`order_customer_credits.refund_amount` / `out_of_band_amount`) — never from timestamps.
 * BOTH fields absent (undefined) means the payload predates the rollout → null, no tag.
 */
export function creditNoteSettlement(
  refundAmount: number | string | null | undefined,
  outOfBandAmount: number | string | null | undefined,
): CreditNoteSettlement {
  if (refundAmount === undefined && outOfBandAmount === undefined) return null;
  if (refundAmount !== null && refundAmount !== undefined && Number(refundAmount) > 0) return 'card_refund';
  if (outOfBandAmount !== null && outOfBandAmount !== undefined && Number(outOfBandAmount) > 0) return 'out_of_band';
  return 'pre_payment';
}

export type ResolveInvoiceUrlFn = (inv: Record<string, unknown>) => string;

/**
 * Demo-feedback fix (AB#20717 round 2, staging: 1,818 vs 1,759) — `amount_due` is the
 * invoice's *current* amount due, which drifts once a credit note is applied against it;
 * it does NOT reflect the invoice's original, frozen total. Precedence, highest first:
 *
 *   1. `inv.stripe_total` — the frozen document total the backend now persists per invoice
 *      (major-unit decimal, same units as `amount_due`/`amount_paid`).
 *   2. `eligibility.invoice.total_minor` — Stripe's own invoice total from the credit-note
 *      eligibility payload (minor units; converted via `minorUnitsToAmount`), used when the
 *      ledger has already fetched eligibility for this invoice but `stripe_total` hasn't
 *      rolled out to this row yet.
 *   3. `amount_due`, then `amount_paid` — the pre-existing fallback ladder (label/behavior
 *      unchanged) for invoices predating both of the above.
 *   4. `null` (renders as "—") when nothing above is present, never a misleading `0`.
 */
export function pickInvoiceAmount(
  inv: Record<string, unknown> | null | undefined,
  eligibility?: CreditNoteEligibility | null,
): number | null {
  const rec = inv as Record<string, unknown> | null | undefined;

  const stripeTotal = rec?.stripe_total;
  if (stripeTotal !== null && stripeTotal !== undefined && Number.isFinite(Number(stripeTotal))) {
    return Number(stripeTotal);
  }

  const eligibleTotalMinor = eligibility?.invoice?.total_minor;
  if (
    eligibleTotalMinor !== null
    && eligibleTotalMinor !== undefined
    && Number.isFinite(Number(eligibleTotalMinor))
  ) {
    const currency = eligibility?.currency
      ?? (typeof rec?.currency === 'string' ? (rec.currency as string) : 'USD');
    return minorUnitsToAmount(Number(eligibleTotalMinor), currency);
  }

  const due = rec?.amount_due;
  if (due !== null && due !== undefined && Number.isFinite(Number(due))) return Number(due);
  const paid = rec?.amount_paid;
  if (paid !== null && paid !== undefined && Number.isFinite(Number(paid))) return Number(paid);
  return null;
}

/**
 * The invoice's frozen PRE-TAX total (`order_item_invoices.subtotal_excl_tax`, AB#21269) — the
 * excl-tax counterpart of `pickInvoiceAmount`'s tax-inclusive `stripe_total`. No fallback ladder
 * here on purpose: `amount_due`/`amount_paid` are tax-inclusive AND drift, so guessing would
 * produce a wrong tax split — `null` simply hides the breakdown (see `netBilled`).
 */
export function pickInvoiceExclTax(inv: Record<string, unknown> | null | undefined): number | null {
  const value = (inv as Record<string, unknown> | null | undefined)?.subtotal_excl_tax;
  if (value !== null && value !== undefined && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
}

/** Sum `lineAllocations`/`line_allocations` quantities for one invoice row, or `null` if absent. */
export function sumInvoicedQty(inv: Record<string, unknown> | null | undefined): number | null {
  const allocations = (inv as Record<string, unknown> | null | undefined)?.lineAllocations
    ?? (inv as Record<string, unknown> | null | undefined)?.line_allocations;
  if (!Array.isArray(allocations) || allocations.length === 0) return null;
  let total = 0;
  for (const row of allocations) {
    total += Number((row as Record<string, unknown> | null | undefined)?.quantity ?? 0);
  }
  return total;
}

/**
 * Negative invoiced-quantity for one existing credit note, derived from eligibility line
 * data — or `null` when it can't be attributed to this specific credit note.
 *
 * `CreditNoteInvoiceLine.already_credited_qty` is a SUM across every credit note on the
 * invoice, not per-CN. It's only safe to attribute the whole sum to `cn` when `cn` is the
 * SOLE entry in `existing_credit_notes` — with two or more prior credit notes there is no
 * way to split the total back out from this payload, so the caller shows "—" instead of a
 * number that could be wrong.
 */
export function creditQtyFromEligibility(
  cn: CreditNoteExistingCnSummary,
  eligibility: CreditNoteEligibility | null | undefined,
): number | null {
  if (!eligibility) return null;
  const existing = eligibility.existing_credit_notes ?? [];
  if (existing.length !== 1 || existing[0]?.id !== cn.id) return null;
  const lines = eligibility.lines ?? [];
  if (!lines.length) return null;
  const total = lines.reduce((sum, line) => sum + Number(line?.already_credited_qty ?? 0), 0);
  return total > 0 ? -total : null;
}

/** Stripe `created` is unix seconds; `order_item_invoices` / `order_customer_credits` timestamps
 *  are ISO strings. `BillingLedgerCreditNoteRow.createdAt` stays unix-seconds throughout (matches
 *  the component's existing `formatCnCreated`), so ISO sources are converted on the way in. */
function unixSecondsFromIso(iso: unknown): number | null {
  if (typeof iso !== 'string' || iso.trim() === '') return null;
  const t = new Date(iso).getTime();
  return Number.isFinite(t) ? Math.floor(t / 1000) : null;
}

/**
 * Build one credit-note row from the embedded `order_item_invoices.credit_notes[]` payload entry
 * (the primary/preferred source — present synchronously, no eligibility fetch required), enriched
 * with the matching eligibility `existing_credit_notes[]` summary when available (for `qty` and the
 * Stripe-hosted `pdfUrl` fallback — see `openCreditNotePdf`, which prefers `credit_note_pdf_s3_key`
 * over this fallback).
 */
function buildCreditNoteRowFromPayload(
  cn: Record<string, unknown>,
  invRowId: string,
  eligibility: CreditNoteEligibility | null | undefined,
  fallbackCurrency: string | null,
  rowIndex: number,
): BillingLedgerCreditNoteRow {
  const stripeId = typeof cn?.stripe_credit_note_id === 'string' ? cn.stripe_credit_note_id : null;
  const matched = stripeId
    ? (eligibility?.existing_credit_notes ?? []).find((e) => e.id === stripeId) ?? null
    : null;
  const idKey = cn?.id !== null && cn?.id !== undefined
    ? String(cn.id)
    : (stripeId ?? String(rowIndex));
  const currency = typeof cn?.currency === 'string'
    ? cn.currency.toUpperCase()
    : fallbackCurrency;
  const amountRaw = cn?.amount;
  const amount = amountRaw !== null && amountRaw !== undefined && Number.isFinite(Number(amountRaw))
    ? -Math.abs(Number(amountRaw))
    : 0;
  const exclTaxRaw = cn?.subtotal_excl_tax;
  const exclTax = exclTaxRaw !== null && exclTaxRaw !== undefined && Number.isFinite(Number(exclTaxRaw))
    ? -Math.abs(Number(exclTaxRaw))
    : null;

  return {
    kind: 'credit_note',
    id: `cn-${idKey}`,
    parentInvoiceId: invRowId,
    number: (typeof cn?.stripe_credit_note_number === 'string' ? cn.stripe_credit_note_number : null)
      ?? stripeId,
    status: matched?.status ?? null,
    createdAt: unixSecondsFromIso(cn?.created_at) ?? matched?.created ?? null,
    amount,
    exclTax,
    settlement: creditNoteSettlement(
      cn?.refund_amount as number | string | null | undefined,
      cn?.out_of_band_amount as number | string | null | undefined,
    ),
    currency,
    qty: matched ? creditQtyFromEligibility(matched, eligibility) : null,
    pdfUrl: matched?.pdf ?? '',
    pdfSource: matched?.pdf_source ?? null,
    raw: cn,
  };
}

/**
 * Build one credit-note row purely from an eligibility `existing_credit_notes[]` summary — the
 * pre-rollout fallback path for invoices whose payload doesn't carry `credit_notes[]` yet.
 */
function buildCreditNoteRowFromEligibility(
  cn: CreditNoteExistingCnSummary,
  invRowId: string,
  eligibility: CreditNoteEligibility | null | undefined,
  fallbackCurrency: string | null,
): BillingLedgerCreditNoteRow {
  return {
    kind: 'credit_note',
    id: `cn-${cn.id}`,
    parentInvoiceId: invRowId,
    number: cn.number,
    status: cn.status,
    createdAt: cn.created ?? null,
    amount: -minorUnitsToAmount(cn.amount_minor, fallbackCurrency ?? 'USD'),
    // Eligibility summaries carry only the tax-inclusive total — no pre-tax figure available.
    exclTax: null,
    settlement: creditNoteSettlement(cn.refund_amount, cn.out_of_band_amount),
    currency: fallbackCurrency,
    qty: creditQtyFromEligibility(cn, eligibility),
    pdfUrl: cn.pdf ?? '',
    pdfSource: cn.pdf_source ?? null,
    raw: cn as unknown as Record<string, unknown>,
  };
}

/**
 * Build the flat, ordered row list the ledger renders: each invoice row immediately followed by
 * its known credit-note rows.
 *
 * Credit-note rows are sourced primarily from the invoice's own `credit_notes[]` (embedded in the
 * `item_invoices` payload — synchronous, no eligibility fetch required, so rows render the moment
 * invoices load / a credit note is optimistically appended). `eligibilityByInvoiceId` — keyed by
 * `String(invoice.id)`, populated by the ledger's lazy fetch — is an ENRICHMENT layer on top (qty
 * derivation, Stripe-hosted pdf fallback), not a prerequisite. When an invoice has no `credit_notes[]`
 * entry at all (pre-rollout rows), eligibility's `existing_credit_notes[]` is used as the sole
 * source, preserving the original (pre-AB#20717-round-2) behavior.
 *
 * Defensive by construction: every new backend field (`invoice_pdf_s3_url`, `stripe_total`,
 * `credit_notes`, `pdf_source`, `lineAllocations`, …) is optional, so rows with none of them still
 * render fully via the existing Stripe-only fallbacks.
 */
export function buildLedgerRows(
  invoices: Record<string, unknown>[] | null | undefined,
  eligibilityByInvoiceId: Record<string, CreditNoteEligibility | undefined> = {},
  resolveUrl: ResolveInvoiceUrlFn = (inv) => resolveInvoiceDocumentUrl(inv),
): BillingLedgerRow[] {
  const rows: BillingLedgerRow[] = [];

  for (const inv of invoices ?? []) {
    const rawInv = inv as Record<string, unknown>;
    const invId = (rawInv?.id as number | string | undefined) ?? null;
    const dedupeKey = invId !== null && invId !== undefined ? String(invId) : String(rawInv?.stripe_invoice_id ?? rows.length);
    const invRowId = `inv-${dedupeKey}`;
    const number = String(rawInv?.stripe_invoice_number ?? rawInv?.stripe_invoice_id ?? 'N/A');
    const eligibility = invId !== null && invId !== undefined ? eligibilityByInvoiceId[String(invId)] : undefined;

    rows.push({
      kind: 'invoice',
      id: invRowId,
      invoiceId: invId,
      number,
      status: (rawInv?.status as string | null | undefined) ?? null,
      createdAt: (rawInv?.created_at as string | null | undefined) ?? null,
      amount: pickInvoiceAmount(rawInv, eligibility),
      exclTax: pickInvoiceExclTax(rawInv),
      currency: (rawInv?.currency as string | null | undefined)
        ? String(rawInv.currency).toUpperCase()
        : null,
      invoicedQty: sumInvoicedQty(rawInv),
      pdfUrl: resolveUrl(rawInv),
      raw: rawInv,
    });

    const cnCurrency = eligibility?.currency ? eligibility.currency.toUpperCase() : null;
    const payloadCreditNotes = Array.isArray(rawInv?.credit_notes)
      ? (rawInv.credit_notes as Record<string, unknown>[])
      : [];

    if (payloadCreditNotes.length > 0) {
      payloadCreditNotes.forEach((cn, idx) => {
        rows.push(buildCreditNoteRowFromPayload(cn, invRowId, eligibility, cnCurrency, idx));
      });
    } else {
      const existingCns = eligibility?.existing_credit_notes ?? [];
      for (const cn of existingCns) {
        rows.push(buildCreditNoteRowFromEligibility(cn, invRowId, eligibility, cnCurrency));
      }
    }
  }

  return rows;
}

export interface NetBilledTotal {
  /** `null` for rows whose currency couldn't be determined. */
  currency: string | null;
  /** Major-unit decimal: Σ invoice amounts + Σ (already-negative) credit-note amounts. Tax-INCLUSIVE. */
  net: number;
  /**
   * Pre-tax net (Σ `exclTax` across the same rows) — `null` unless EVERY summed row carried an
   * `exclTax` figure, so a partially-backfilled ledger never shows a wrong tax split (AB#21269).
   */
  exclTax: number | null;
  /** `net - exclTax` when `exclTax` is available, else `null`. */
  tax: number | null;
}

/**
 * Σ invoice amounts − Σ credit amounts. Credit-note rows already carry a negative
 * `amount` (see `buildLedgerRows`), so this is a plain per-currency sum; rows with
 * `amount === null` (undeterminable) are skipped rather than treated as `0`.
 *
 * Currency handling: an order's billing documents are effectively single-currency in
 * practice (one Stripe customer, one company currency), but each ledger/credit-note row
 * carries its own `currency` per Stripe's data model, so this stays defensive rather than
 * assuming uniformity:
 *   - All rows sharing one currency -> a single-entry array (the common case; the footer
 *     renders it as a single "Net billed: X.XX USD").
 *   - Mixed (or partially unknown) currencies -> one entry per currency actually seen, so
 *     the footer renders one total per currency instead of silently summing across units.
 */
export function netBilled(rows: BillingLedgerRow[]): NetBilledTotal[] {
  const totals = new Map<string | null, { net: number; exclTax: number; complete: boolean }>();
  for (const row of rows) {
    if (row.amount === null || row.amount === undefined) continue;
    const currency = row.currency ? row.currency.toUpperCase() : null;
    const entry = totals.get(currency) ?? { net: 0, exclTax: 0, complete: true };
    entry.net += row.amount;
    // AB#21269: the excl-tax/tax split only renders when EVERY contributing row carries its own
    // pre-tax figure — one missing row (legacy, not yet swept) hides the split rather than
    // showing a subtly wrong one.
    if (row.exclTax === null || row.exclTax === undefined) {
      entry.complete = false;
    } else {
      entry.exclTax += row.exclTax;
    }
    totals.set(currency, entry);
  }
  return Array.from(totals.entries()).map(([currency, entry]) => ({
    currency,
    net: entry.net,
    exclTax: entry.complete ? entry.exclTax : null,
    tax: entry.complete ? entry.net - entry.exclTax : null,
  }));
}

/** Narrowed shape of one `order_item_invoices.credit_notes[]` entry — see contract in the module doc. */
export interface RawCreditNoteFields {
  id: number | string | null;
  stripe_credit_note_id: string | null;
  stripe_credit_note_number: string | null;
  /** Tax-inclusive Stripe total (AB#20717/20853 tax fix) — matches `amount_paid`/`stripe_total`
   *  units and is what the ledger's `netBilled` sums. */
  amount: number | string | null;
  /**
   * Pre-tax requested total (AB#20717/20853 tax fix) — the PRE-TAX per-item totals-row basis
   * (`liveCreditTotalsForOrderItem`). `null`/absent on rows created before the backend added this
   * field and not yet touched by the hourly backfill sweep; those legacy rows still carry the
   * PRE-TAX value in `amount` itself, so callers must fall back to `amount` in that case (see
   * `liveCreditTotalsForOrderItem`) — never read `amount` here for tax-basis math.
   */
  subtotal_excl_tax?: number | string | null;
  /** Settlement allocation recorded at creation (AB#21269) — see {@link creditNoteSettlement}. */
  refund_amount?: number | string | null;
  out_of_band_amount?: number | string | null;
  currency: string | null;
  credit_note_pdf_s3_key: string | null;
  created_at: string | null;
}

/**
 * Normalize a freshly created credit note (the wizard's `credit-note-created` event payload —
 * `CreateStripeCreditNoteModal.vue`'s `rawCreditRow`, i.e. the backend's `result.credit`) into the
 * same shape as an embedded `order_item_invoices.credit_notes[]` entry, narrowing to known fields
 * only so unrelated fields on the created-credit response don't leak into the ledger/pill rows.
 */
export function normalizeCreditNoteRow(raw: Record<string, unknown> | null | undefined): RawCreditNoteFields {
  const r = raw ?? {};
  const id = r.id;
  const amount = r.amount;
  const subtotalExclTax = r.subtotal_excl_tax;
  return {
    id: (typeof id === 'number' || typeof id === 'string') ? id : null,
    stripe_credit_note_id: typeof r.stripe_credit_note_id === 'string' ? r.stripe_credit_note_id : null,
    stripe_credit_note_number: typeof r.stripe_credit_note_number === 'string' ? r.stripe_credit_note_number : null,
    amount: (typeof amount === 'number' || typeof amount === 'string') ? amount : null,
    subtotal_excl_tax: (typeof subtotalExclTax === 'number' || typeof subtotalExclTax === 'string')
      ? subtotalExclTax
      : null,
    // Settlement allocation: preserve absent-vs-null (absent = old payload → no tag; null = known
    // pre-payment CN), so only pass through when the key exists on the source record.
    ...('refund_amount' in r
      ? { refund_amount: (typeof r.refund_amount === 'number' || typeof r.refund_amount === 'string') ? r.refund_amount : null }
      : {}),
    ...('out_of_band_amount' in r
      ? { out_of_band_amount: (typeof r.out_of_band_amount === 'number' || typeof r.out_of_band_amount === 'string') ? r.out_of_band_amount : null }
      : {}),
    currency: typeof r.currency === 'string' ? r.currency : null,
    credit_note_pdf_s3_key: typeof r.credit_note_pdf_s3_key === 'string' ? r.credit_note_pdf_s3_key : null,
    created_at: typeof r.created_at === 'string' ? r.created_at : null,
  };
}

/**
 * Optimistic UI update (AB#20717 round 2 fix #4 — "live refresh"): append a freshly created
 * credit note to the matching invoice's `credit_notes[]` array immutably (new array + new invoice
 * object for the matched row only; every other invoice object keeps referential identity) so
 * callers can reassign into a reactive Vue structure without extra `.push` mutation semantics.
 *
 * No-op (returns the original `invoices` reference) when `invoiceId`/`creditRow` is missing or no
 * invoice with that id is found — callers should treat that as "nothing to optimistically update"
 * rather than an error; the eventual `notifyParentOrderDataUpdated()` refetch is still the source
 * of truth.
 */
export function appendCreditNoteToInvoice(
  invoices: Record<string, unknown>[],
  invoiceId: number | string | null | undefined,
  creditRow: Record<string, unknown> | null | undefined,
): Record<string, unknown>[] {
  if (invoiceId === null || invoiceId === undefined || !creditRow) return invoices;
  const targetKey = String(invoiceId);
  let matched = false;

  const next = invoices.map((inv) => {
    const rec = inv as Record<string, unknown>;
    if (String(rec?.id ?? '') !== targetKey) return inv;
    matched = true;
    const existing = Array.isArray(rec.credit_notes) ? (rec.credit_notes as unknown[]) : [];
    return {
      ...rec,
      credit_notes: [...existing, normalizeCreditNoteRow(creditRow)],
    };
  });

  return matched ? next : invoices;
}

export interface LiveCreditTotals {
  amount: number;
  count: number;
}

/**
 * Sum credit notes that belong to invoices which STILL EXIST on the order item — i.e. the nested
 * `credit_notes[]` the backend attaches to each live `item_invoices` row.
 *
 * Why not sum the flat `order_customer_credits` table (the pre-AB#20717 approach)? Credit rows are
 * an audit trail and are never deleted — when an invoice is removed via "Remove Invoice" and the
 * item re-invoiced, the old invoice's credits stay in the table as orphans. Summing the flat list
 * counted those ghost credits, over-stated "credited" past the item total, and clamped the
 * totals-row net to 0.00 while the ledger's Net billed (built from live invoices) disagreed.
 * Both displays must read the same source: credits under live invoice rows.
 *
 * Returns `{ amount: 0, count: 0 }` — never a misleading stale total — whenever there's simply
 * nothing live to sum: an empty `invoices` list, or invoice rows that carry no `credit_notes`
 * field at all. Every payload has carried nested `credit_notes` since AB#20717 wave 6, so "field
 * absent" now means "this row has no live credits", not "old payload, fall back to the flat
 * table". Round 3 fix (order #26529): after an invoice + its credit note were removed, the old
 * "field absent ⇒ null ⇒ fall back to the flat `order_customer_credits` table" path resurrected
 * the removed invoice's orphaned credit row and rendered a ghost "~~100~~ 50 CAD" totals line for
 * an item that no longer had any live invoice or credit against it.
 *
 * `null` is now reserved for genuinely invalid input — a falsy `orderItemId` or a non-array
 * `invoices` — which callers should treat as "can't determine" rather than "zero credits".
 *
 * Tax basis (AB#20717/20853 tax fix): this function feeds the PRE-TAX per-item totals row (as
 * opposed to the ledger's `netBilled`, which sums the tax-inclusive `amount`). Each credit is
 * summed via `subtotal_excl_tax ?? amount` — new rows (post-backend-fix) carry both fields with
 * `subtotal_excl_tax` holding the pre-tax figure; rows created before the fix (and not yet
 * touched by the hourly backfill sweep) have `subtotal_excl_tax` null/absent and still carry the
 * pre-tax value directly in `amount`, so the fallback keeps old rows correct until backfilled.
 */
export function liveCreditTotalsForOrderItem(
  invoices: Array<Record<string, unknown>> | null | undefined,
  orderItemId: number | string | null | undefined,
): LiveCreditTotals | null {
  const id = Number(orderItemId ?? 0);
  if (!id || !Array.isArray(invoices)) return null;

  let amount = 0;
  let count = 0;

  for (const inv of invoices) {
    const rec = inv as Record<string, unknown>;
    // The raw jsonb payload does not embed order_item_id inside each invoice object — callers
    // pre-scope the list to one order item. Filter only when the field is actually present.
    const rowItemId = Number(rec?.order_item_id ?? 0);
    if (rowItemId && rowItemId !== id) continue;
    if (!('credit_notes' in rec)) continue;
    const credits = Array.isArray(rec.credit_notes) ? (rec.credit_notes as unknown[]) : [];
    for (const c of credits) {
      const creditRec = c as Record<string, unknown>;
      const subtotalExclTax = creditRec?.subtotal_excl_tax;
      const basis = subtotalExclTax !== null && subtotalExclTax !== undefined
        ? subtotalExclTax
        : creditRec?.amount;
      const amt = Number(basis ?? 0);
      if (Number.isFinite(amt) && amt > 0) {
        amount += amt;
        count += 1;
      }
    }
  }

  return { amount, count };
}
